import React from "react"
import PropTypes from "prop-types"
import Slider from "react-slick"
import { useTheme } from "@mui/material/styles"
import Card from "@mui/material/Card"
import Box from "@mui/material/Box"
import useMediaQuery from "@mui/material/useMediaQuery"
import CardContent from "@mui/material/CardContent"
import Typography from "@mui/material/Typography"
import Avatar from "@mui/material/Avatar"
import Grid from "@mui/material/Grid"
import { colors } from "@mui/material"
import PersonOutlineIcon from "@mui/icons-material/PersonOutline"
import SearchIcon from "@mui/icons-material/Search"
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange"

const Process = ({ themeMode = "light" }) => {
    const theme = useTheme()
    const isMd = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true,
    })

    const GridItemHeadlineBlock = () => (
        <Box>
            <Typography
                sx={{
                    textTransform: "uppercase",
                    fontWeight: "medium",
                }}
                gutterBottom
                color={"secondary"}
            >
                Our Approach
            </Typography>
            <Box
                component={Typography}
                fontWeight={700}
                variant={"h3"}
                color={"textPrimary"}
                gutterBottom
                data-aos={isMd ? "fade-right" : "fade-up"}
            >
                Streamlined and Client-Focused Real Estate Process
            </Box>
            <Typography
                variant={"h6"}
                component={"p"}
                color={"textSecondary"}
                data-aos={isMd ? "fade-right" : "fade-up"}
            >
                We prioritize your needs and preferences with a tailored
                approach that ensures a smooth journey from start to finish. Our
                dedicated team will guide you through every step of buying or
                selling your property.
            </Typography>
        </Box>
    )

    const GridItemReviewBlock = () => {
        const sliderOpts = {
            dots: true,
            infinite: true,
            speed: 500,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
        }

        return (
            <Box maxWidth={"100%"} data-aos={isMd ? "fade-left" : "fade-up"}>
                <Slider {...sliderOpts}>
                    {[
                        {
                            title: "Consultation",
                            subtitle:
                                "We start with a comprehensive consultation to understand your goals and preferences, offering personalized advice to align with your real estate needs.",
                            icon: <PersonOutlineIcon sx={{ fontSize: 48 }} />,
                            color: colors.amber,
                        },
                        {
                            title: "Property Search & Viewing",
                            subtitle:
                                "Discover and tour properties that meet your criteria. We provide insights and feedback to help you find the perfect home.",
                            icon: <SearchIcon sx={{ fontSize: 48 }} />,
                            color: colors.purple,
                        },
                        {
                            title: "Transaction Management",
                            subtitle:
                                "We manage every aspect of the transaction process, from negotiations to closing, ensuring a seamless experience and exceptional outcomes.",
                            icon: (
                                <CurrencyExchangeIcon sx={{ fontSize: 48 }} />
                            ),
                            color: colors.green,
                        },
                    ].map((item, i) => (
                        <Box padding={{ xs: 1, sm: 2 }} key={i}>
                            <Box
                                component={Card}
                                boxShadow={{ xs: 1, sm: 3 }}
                                borderRadius={5}
                                padding={{ xs: 1, sm: 2, md: 3 }}
                            >
                                <Box
                                    component={CardContent}
                                    display={"flex"}
                                    flexDirection={"column"}
                                >
                                    <Box
                                        component={Avatar}
                                        variant="rounded"
                                        width={84}
                                        height={84}
                                        marginBottom={2}
                                        bgcolor={
                                            themeMode === "light"
                                                ? item.color[50]
                                                : item.color[200]
                                        }
                                        borderRadius={5}
                                    >
                                        <Box
                                            color={
                                                item.color[
                                                    themeMode === "light"
                                                        ? 500
                                                        : 700
                                                ]
                                            }
                                        >
                                            {item.icon}
                                        </Box>
                                    </Box>
                                    <Box
                                        component={Typography}
                                        variant={"h6"}
                                        gutterBottom
                                        fontWeight={500}
                                    >
                                        {item.title}
                                    </Box>
                                    <Typography color="text.secondary">
                                        {item.subtitle}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    ))}
                </Slider>
            </Box>
        )
    }

    return (
        <Box>
            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Box
                        width={1}
                        height="100%"
                        display="flex"
                        alignItems="center"
                    >
                        <GridItemHeadlineBlock />
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box
                        width={1}
                        height="100%"
                        display="flex"
                        alignItems="center"
                    >
                        <GridItemReviewBlock />
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

Process.propTypes = {
    themeMode: PropTypes.string.isRequired,
}

export default Process
